export type TextSegment =
  | { type: 'text'; value: string }
  | { type: 'link'; value: string; href: string };

const URL_PATTERN = /https?:\/\/[^\s<>"']+/gi;
const TRAILING_SENTENCE_PUNCTUATION_PATTERN = /[,.!?;:]+$/;

const CLOSING_TO_OPENING_WRAPPER = {
  ')': '(',
  ']': '[',
  '}': '{',
} as const;

type ClosingWrapper = keyof typeof CLOSING_TO_OPENING_WRAPPER;

function isClosingWrapper(char: string): char is ClosingWrapper {
  return char in CLOSING_TO_OPENING_WRAPPER;
}

function countOccurrences(text: string, char: string): number {
  let count = 0;

  for (const current of text) {
    if (current === char) {
      count += 1;
    }
  }

  return count;
}

function stripUnbalancedTrailingWrappers(url: string): {
  url: string;
  trailing: string;
} {
  let result = url;
  let trailing = '';

  while (result.length > 0) {
    const lastChar = result.at(-1);

    if (!lastChar || !isClosingWrapper(lastChar)) {
      break;
    }

    const openingChar = CLOSING_TO_OPENING_WRAPPER[lastChar];
    const openingCount = countOccurrences(result, openingChar);
    const closingCount = countOccurrences(result, lastChar);

    if (closingCount <= openingCount) {
      break;
    }

    result = result.slice(0, -1);
    trailing = `${lastChar}${trailing}`;
  }

  return { url: result, trailing };
}

function normalizeUrlMatch(raw: string): {
  url: string;
  trailing: string;
} {
  const punctuationMatch = raw.match(TRAILING_SENTENCE_PUNCTUATION_PATTERN);
  let trailing = punctuationMatch?.[0] ?? '';
  let url = trailing ? raw.slice(0, -trailing.length) : raw;

  const wrappers = stripUnbalancedTrailingWrappers(url);
  url = wrappers.url;
  trailing = `${wrappers.trailing}${trailing}`;

  return { url, trailing };
}

function isUsableHttpUrl(url: string): boolean {
  return /^https?:\/\/.+/i.test(url);
}

function pushTextSegment(segments: TextSegment[], value: string): void {
  if (!value) {
    return;
  }

  const lastSegment = segments.at(-1);

  if (lastSegment?.type === 'text') {
    lastSegment.value += value;
    return;
  }

  segments.push({ type: 'text', value });
}

export function splitTextWithUrls(text: string): TextSegment[] {
  if (!text) {
    return [];
  }

  const segments: TextSegment[] = [];
  let lastIndex = 0;

  for (const match of text.matchAll(URL_PATTERN)) {
    if (match.index === undefined) {
      continue;
    }

    const raw = match[0];
    const matchIndex = match.index;

    if (matchIndex > lastIndex) {
      pushTextSegment(segments, text.slice(lastIndex, matchIndex));
    }

    const { url, trailing } = normalizeUrlMatch(raw);

    if (isUsableHttpUrl(url)) {
      segments.push({
        type: 'link',
        value: url,
        href: url,
      });
    } else if (url) {
      pushTextSegment(segments, url);
    }

    pushTextSegment(segments, trailing);

    lastIndex = matchIndex + raw.length;
  }

  if (lastIndex < text.length) {
    pushTextSegment(segments, text.slice(lastIndex));
  }

  return segments;
}
